/**
 * Catálogo nacional de medicamentos MSPAS (Guatemala).
 * Mismo modelo de referencia que la farmacia con POS del hospital nacional:
 * catálogo oficial + inventario de mostrador + receta + cobro FEL/recibo.
 */
import raw from '../data/mspas-medicamentos-catalog.json';

export type MedicamentoMspas = {
  codigo: string;
  nombre: string;
  precioMin: number;
  precioMax: number;
  precioModa: number;
  precioPromedio: number;
  farmaciasCount: number;
  farmacias: string[];
};

export const CATALOGO_MEDICAMENTOS_MSPAS: MedicamentoMspas[] = raw as MedicamentoMspas[];

const byCodigo = new Map(CATALOGO_MEDICAMENTOS_MSPAS.map(m => [m.codigo.toUpperCase(), m]));

/** Precio sugerido POS clínica (quetzales por unidad). */
export function precioSugeridoClinica(m: MedicamentoMspas): number {
  const p = m.precioPromedio;
  if (p >= 0.05 && p <= 500) return Math.round(p * 100) / 100;
  if (m.precioMin >= 0.05) return m.precioMin;
  const moda = m.precioModa;
  return moda > 50 ? Math.round(moda) / 100 : moda;
}

export function getMedicamentoByCodigo(codigo: string): MedicamentoMspas | undefined {
  return byCodigo.get(codigo.trim().toUpperCase());
}

export function searchMedicamentosMspas(query: string, limit = 24): MedicamentoMspas[] {
  const q = query.trim().toUpperCase();
  if (!q) return CATALOGO_MEDICAMENTOS_MSPAS.slice(0, limit);
  const exact = byCodigo.get(q);
  const out: MedicamentoMspas[] = exact ? [exact] : [];
  for (const m of CATALOGO_MEDICAMENTOS_MSPAS) {
    if (out.length >= limit) break;
    if (m.codigo.toUpperCase().startsWith(q) && m !== exact) out.push(m);
  }
  if (out.length < limit) {
    const words = q.split(/\s+/).filter(Boolean);
    for (const m of CATALOGO_MEDICAMENTOS_MSPAS) {
      if (out.length >= limit) break;
      if (out.includes(m)) continue;
      const n = m.nombre.toUpperCase();
      if (words.every(w => n.includes(w))) out.push(m);
    }
  }
  return out;
}

/** Medicamentos de alta rotación en clínicas guatemaltecas. */
export const MEDICAMENTOS_ALTA_ROTACION = [
  'A004', 'A022', 'A010', 'I001', 'L010', 'M011', 'O002', 'D003', 'D021',
  'C008', 'A026', 'L003', 'A012', 'C023', 'A053', 'E002', 'M007', 'C060',
  'A051', 'B006', 'S005', 'A050', 'C005', 'H012', 'A001', 'D010', 'L011',
  'C010', 'F003', 'A052', 'R009', 'S027', 'J002', 'N011', 'A054', 'C019',
  'D036', 'M012', 'G014', 'H003',
];

export function medicamentoToProductoSeed(m: MedicamentoMspas, stock = 40) {
  const rx = /\b(AMOXICILINA|CEFTRIAXONA|METFORMINA|INSULINA|PENICILINA|CLINDAMICINA|AZITROMICINA|CIPROFLOXACINA|AMLODIPINA|LOSARTAN|METRONIDAZOL|CLARITROMICINA|CEFIXIMA|ACICLOVIR 800|PREGABALINA|SERTRALINA|ESCITALOPRAM|CARBAMAZEPINA|ACIDO VALPROICO)\b/i.test(m.nombre);
  return {
    codigo: m.codigo,
    nombre: m.nombre,
    categoria: 'MEDICAMENTO' as const,
    precio: precioSugeridoClinica(m),
    stock,
    unidad: unidadFromNombre(m.nombre),
    requiereReceta: rx,
    afectaInventario: true,
    activo: true,
  };
}

function unidadFromNombre(nombre: string): string {
  const u = nombre.toUpperCase();
  if (u.includes('JARABE') || u.includes('SUSPENSION') || u.includes('FRASCO')) return 'FRASCO';
  if (u.includes('VIAL') || u.includes('AMPOLLA')) return 'UNIDAD';
  if (u.includes('CREMA') || u.includes('TUBO') || u.includes('GEL')) return 'TUBO';
  if (u.includes('SOBRE')) return 'SOBRE';
  return 'UNIDAD';
}
